import React, { useState } from 'react';
import { Link, useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { Container, Nav, Navbar, Button } from 'react-bootstrap';
import { 
  List, 
  House, 
  Images, 
  Gear, 
  PersonLinesFill,
  BoxArrowRight,
  ChatDots,
} from 'react-bootstrap-icons';
import authService from '../services/authService';
import DashboardPage from '../pages/DashboardPage';
import './AdminLayout.css';
import useTokenMonitor from '../hooks/useTokenMonitor';
import ExhibitionsManagePage from '../pages/ExhibitionsManagePage';
import CountryExhibitionsPage from '../pages/CountryExhibitionsPage';
import ExhibitionPhotosPage from '../pages/ExhibitionPhotosPage';
import ContactSettingsPage from '../pages/ContactSettingsPage';
import ApplicationsManagePage from '../pages/ApplicationsManagePage';
import ContactMessagesPage from '../pages/ContactMessagesPage';
import FabricsManagePage from '../pages/FabricsManagePage';

const AdminLayout = () => {
  useTokenMonitor(); 
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin/login');
  };

  // Function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/exhibitions')) {
      if (path.includes('/photos/')) return 'Exhibition Photos';
      if (path.includes('/country/')) return 'Country Exhibitions';
      return 'Manage Exhibitions';
    }
    if (path.includes('/fabrics')) return 'Manage Fabrics';
    if (path.includes('/contact-settings')) return 'Contact Settings';
    if (path.includes('/contact-messages')) return 'Contact Messages';
    if (path.includes('/applications')) return 'Job Applications';
    return 'Admin Panel';
  };

  return (
    <div className="d-flex flex-column">
      {/* Top Header with Logout - Fixed at the very top */}
      <div className="border-bottom d-flex justify-content-between align-items-center px-4 py-2" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001, backgroundColor: '#212529' }}>
        <div className="d-flex align-items-center logo-wrapper">
          <img 
            src="/static/media/tekstas-logo.bb2c6fb7f50bda571dc3.svg" 
            alt="Tekstas Logo" 
            style={{ height: '40px', width: 'auto', filter: 'brightness(0) invert(1)' }}
          />
        </div>
      </div>

      <div className="d-flex" style={{ marginTop: '56px' }}>
        {/* Sidebar */}
        <div className={`bg-dark text-white admin-sidebar ${showSidebar ? 'show' : ''}`}>
          <div className="p-3 d-flex flex-column h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">Admin Panel</h4>
              <Button variant="link" className="text-white d-md-none p-0" onClick={() => setShowSidebar(false)}>
                ×
              </Button>
            </div>
            <Nav className="flex-column flex-grow-1" style={{backgroundColor: '#ffffff !important'}}>
              <Nav.Link as={Link} to="/admin/dashboard" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
                <House className="me-2" size={18} />
                Dashboard
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/exhibitions" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
                <Images className="me-2" size={18} />
                Manage Exhibitions
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/fabrics" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
                <Images className="me-2" size={18} />
                Manage Fabrics
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/contact-messages" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
              <ChatDots className="me-2" size={18} />
                Contact Messages
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/contact-settings" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
                <Gear className="me-2" size={18} />
                Contact Settings
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/applications" className="text-white d-flex align-items-center" onClick={() => setShowSidebar(false)}>
                <PersonLinesFill className="me-2" size={18} />
                Job Applications
              </Nav.Link>
            </Nav>
            <div className="mt-auto pt-3">
              <Button 
                className="w-100 d-flex align-items-center justify-content-center" 
                onClick={handleLogout}
                style={{ backgroundColor: '#555555', borderColor: '#555555' }}
              >
                <BoxArrowRight className="me-2" size={16} />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-grow-1 admin-content ${showSidebar ? 'show' : ''}`}>
          <Navbar className="shadow-sm navbar-bg flex justify-content-start">
            <Container style={{margin: '0px', backgroundColor: 'transparent'}}>
              <div className="d-flex align-items-center justify-content-between w-100">
                <Button 
                  variant="outline-dark" 
                  className="d-md-none me-3"
                  onClick={() => setShowSidebar(!showSidebar)}
                >
                  <List size={24} />
                </Button>
                <h4 className="mb-0 text-white">{getPageTitle()}</h4>
                
              </div>
              <div className="ms-auto">
                
              </div>
            </Container>
          </Navbar>

          <Container fluid className="p-4 bg-light" style={{ minHeight: 'calc(100vh - 56px)' }}>
            <Routes>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="exhibitions" element={<ExhibitionsManagePage />} />
              <Route path="exhibitions/country/:countryId" element={<CountryExhibitionsPage />} />
              <Route path="exhibitions/photos/:exhibitionId" element={<ExhibitionPhotosPage />} />
              <Route path="fabrics" element={<FabricsManagePage />} />
              <Route path="contact-settings" element={<ContactSettingsPage />} />
              <Route path="contact-messages" element={<ContactMessagesPage />} />
              <Route path="applications" element={<ApplicationsManagePage />} />
            </Routes>
          </Container>
        </div>

        {/* Backdrop */}
        {showSidebar && (
          <div 
            className="d-md-none" 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999
            }}
            onClick={() => setShowSidebar(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminLayout;